import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type JSX } from 'react'
import {
  ApiError,
  api,
  can,
  daysUntil,
  refusalReason,
  shortDate,
  type ExceptionChain,
  type Finding,
  type Issue,
  type Me,
  type Vendor,
} from '../lib/api'
import { statusWord } from '../lib/status'
import { DataTable, ErrorBox, ListCount, Loading, type Column } from '../components/DataTable'
import { ExceptionChainPanel } from '../components/ExceptionChain'
import { ExceptionWorkflowPanel } from '../components/ExceptionWorkflow'
import { acceptanceFooter, decideAcceptance, exceptionBody } from '../components/footers'
import { Dialog, MiniTrack, Status, Toggletip, Picker, type StageState, type Tone, type PickerOption } from '../components/kit'
import { AssessTabs } from '../components/assess/AssessTabs'
import { afterEdit, fromServer, type Listed } from '../lib/issues'
import { useSave } from '../lib/save'
import { VendorChoice } from '../components/VendorChoice'
import { personLabel, usePeopleNames } from '../lib/people'
import { checkAfterToday, checkNotAfterToday, checkTargetClosure, todayIso } from '../lib/dateRules'
import {
  exceptionsApi,
  personaNames,
  SEVERITIES,
  severityTone,
  type DDException,
  type DDParameter,
  type ExceptionWorkflow,
} from '../lib/exceptions'
import {
  isContractWaiver,
  isRiskAcceptance,
  isVendorWide,
  kindLabel,
  ratingPhrase,
  ratingWords,
  waiverStands,
} from '../lib/acceptance'
import '../styles/prose.css'
import '../styles/exceptions.css'
import { ErrorReferenceOf } from '../components/ui'

// The register of risks somebody decided to carry rather than fix, and the
// vendor due diligence exceptions of the product officer's section 4: each
// raised under a parameter with a severity, reviewed by the function the
// parameter names, decided by the authority for its severity, and closed on
// validated evidence. An exception raised before that workflow reads and is
// decided exactly as it was, through the approval matrix chain.
//
// # WHAT THE SCREEN SAYS AT A GLANCE
//
// Each acceptance is one row: what it accepts, where its sign-off chain is,
// when it runs out and its status. The reasons an approver has to read before
// deciding (the justification, the compensating control, the chain with who
// may sign each stage) open in a drawer from the row, which is where the
// decision is made. Before this they were printed in full for every row, so a
// register of twenty was a page nobody could scan.

// An exception is a documented decision to accept a finding rather than fix
// it, so it is raised against the finding itself: approving one moves that
// finding to accepted and its expiry is what reopens it. Where no finding
// stands, the gate key is the fallback, because a free text exception never
// joins back to the gap it excuses and a year later nobody can say which one
// was accepted. These are the keys the shipped risk models carry.
const CATALOGUE_GATES: { key: string; what: string }[] = [
  { key: 'DPDPA-PROC-01', what: 'No written processing agreement' },
  { key: 'DPDPA-DEL-01', what: 'No return or deletion of our data on termination' },
  { key: 'SEC-MFA-01', what: 'No multi-factor authentication on administrative access' },
  { key: 'BCP-UNTESTED-01', what: 'Continuity plan never tested' },
  { key: 'CERTIN-NOTIFY-01', what: 'Breach notice window longer than six hours' },
  { key: 'CERTIN-LOGS-01', what: 'Logs not retained 180 days within India' },
  { key: 'RBI-AUDIT-01', what: 'No right to audit for us, our auditors or the regulator' },
  { key: 'RBI-LOCALITY-01', what: 'Regulated workload data held wholly outside India' },
  { key: 'SEBI-SUBPROC-01', what: 'Subcontractors carry no equivalent obligations' },
]

// A toggletip holds 200 characters. A sentence the API wrote can be longer;
// it is cut at a word here and shown whole wherever there is room for it.
function tip(text: string): string {
  const t = text.trim()
  if (t.length <= 200) return t
  const cut = t.slice(0, 196)
  const space = cut.lastIndexOf(' ')
  return (space > 140 ? cut.slice(0, space) : cut) + '...'
}

// An acceptance that has run out, whichever way the register records it. The
// worker writes EXPIRED on the ones that carry a finding, because ending those
// is how the finding gets handed back; its sweep joins the findings table, so
// an exception raised against nothing stays APPROVED with a date in the past
// and nobody ever writes anything on it. Both belong in the same queue, which
// exists to catch an acceptance nobody is accountable for any more.
function hasLapsed(x: DDException): boolean {
  return x.status === 'EXPIRED' || (x.status === 'APPROVED' && x.expired)
}

// The state in the vocabulary's words (lib/status.ts). An approval past its
// expiry reads Expired whether or not the worker has ended it yet: "Lapsed" and
// "Expired" were two words for one fact, and older rows' "Accepted" a third
// word for Approved.
function statusOf(x: DDException): { text: string; tone: Tone } {
  const s = hasLapsed(x) ? statusWord('exception', 'EXPIRED') : statusWord('exception', x.status)
  return { text: s.word, tone: s.tone }
}

// The expiry as a label: critical once it has passed, a warning inside thirty
// days, quiet otherwise. A rejected acceptance has no expiry worth watching.
function expiryOf(x: DDException): { text: string; tone: Tone } | null {
  if (x.status === 'REJECTED' || x.status === 'CLOSED') return null
  const days = daysUntil(x.expiresOn)
  if (days === null) return null
  if (days < 0) return { text: 'Expired', tone: 'crit' }
  if (days === 0) return { text: 'Expires today', tone: 'warn' }
  return { text: 'Expires in ' + days + (days === 1 ? ' day' : ' days'), tone: days < 30 ? 'warn' : 'plain' }
}

// WAIVERS: every contract waiver. The founder's rule is that a waiver is
// findable until the contract is filed, so the register keeps a tile for them
// and counts the ones that stand (lib/acceptance.ts waiverStands).
type View = 'ALL' | 'MINE' | 'PENDING' | 'APPROVED' | 'CLOSURE' | 'REJECTED' | 'LAPSED' | 'WAIVERS'

// Waiting on somebody: raised, in review, escalated, or an older one pending
// its chain. The Pending tile counts all of them.
function deciding(x: DDException): boolean {
  return x.status === 'PENDING' || x.status === 'IN_REVIEW' || x.status === 'ESCALATED'
}

// A row for an exception the reader reaches only because it waits on them:
// the executive, who cannot open the register, sees these and nothing else.
function rowOf(wf: ExceptionWorkflow): DDException {
  return {
    id: wf.id,
    vendorId: wf.vendorId,
    vendorName: wf.vendorName,
    title: wf.title,
    justification: '',
    compensatingControl: '',
    ownerUserId: '',
    status: wf.status,
    requestedBy: wf.requestedBy,
    expiresOn: wf.expiresOn,
    expired: wf.expired,
    findingId: wf.findingId ?? null,
    gateKey: wf.gateKey ?? null,
    workflow: wf,
  }
}

function trackStates(wf: ExceptionWorkflow): StageState[] {
  return wf.track.map((t) =>
    t.state === 'stopped' ? (wf.status === 'REJECTED' || wf.status === 'REMEDIATION_REQUIRED' ? 'rejected' : 'blocked') : t.state,
  )
}


type Draft = {
  vendorId: string
  findingId: string
  gateKey: string
  title: string
  justification: string
  compensatingControl: string
  ownerUserId: string
  expiresOn: string
  parameterCode: string
  severity: string
  correctiveAction: string
  targetClosureOn: string
  vendorResponse: string
  vendorResponseOn: string
}

const EMPTY_DRAFT: Draft = {
  vendorId: '',
  findingId: '',
  gateKey: '',
  title: '',
  justification: '',
  compensatingControl: '',
  ownerUserId: '',
  expiresOn: '',
  parameterCode: '',
  severity: '',
  correctiveAction: '',
  targetClosureOn: '',
  vendorResponse: '',
  vendorResponseOn: '',
}

function isoDay(offsetDays: number): string {
  const d = new Date()
  // Midday avoids a date that shifts by one when the browser is west of UTC.
  d.setHours(12, 0, 0, 0)
  d.setDate(d.getDate() + offsetDays)
  return d.toISOString().slice(0, 10)
}

// The field names match the API's own issue fields, so a client-side check and
// a 422 from the server share one summary and one set of anchors.
function checkDraft(d: Draft): Issue[] {
  const out: Issue[] = []
  if (!d.vendorId) {
    out.push({ field: 'vendorId', code: 'REQUIRED', detail: 'An exception is always against a vendor.' })
  }
  if (!d.parameterCode) {
    out.push({ field: 'parameterCode', code: 'REQUIRED', detail: 'Choose the due diligence parameter this falls under.' })
  }
  if (!d.severity) {
    out.push({ field: 'severity', code: 'REQUIRED', detail: 'Choose the severity.' })
  }
  if (!d.title.trim()) {
    out.push({ field: 'title', code: 'REQUIRED', detail: 'Name the gap in a line somebody can find later.' })
  }
  if (!d.justification.trim()) {
    out.push({ field: 'justification', code: 'REQUIRED', detail: 'Say why this risk is being accepted.' })
  }
  if (!d.compensatingControl.trim()) {
    out.push({
      field: 'compensatingControl',
      code: 'REQUIRED',
      detail: 'Name the compensating control. Without one this is an unmanaged gap.',
    })
  }
  if (!d.ownerUserId.trim()) {
    out.push({ field: 'ownerUserId', code: 'REQUIRED', detail: 'Somebody owns this until it expires.' })
  }
  if (!d.expiresOn) {
    out.push({ field: 'expiresOn', code: 'REQUIRED', detail: 'An exception without an expiry is never revisited.' })
  }
  return [...out, ...dateIssues(d)]
}

// D20 of the date rules: the expiry after today, the target closure between
// today and the expiry, the vendor's response not in the future. The server
// holds the same three; here they are said when the person leaves the field.
function dateIssues(d: Draft): Issue[] {
  const today = todayIso()
  const out: Issue[] = []
  const expiry = checkAfterToday('Expiry', d.expiresOn, today)
  if (expiry) out.push({ field: 'expiresOn', code: 'IN_THE_PAST', detail: expiry })
  const target = checkTargetClosure(d.targetClosureOn, d.expiresOn, today)
  if (target) out.push({ field: 'targetClosureOn', code: 'OUT_OF_RANGE', detail: target })
  const response = checkNotAfterToday('Response received on', d.vendorResponseOn, today)
  if (response) out.push({ field: 'vendorResponseOn', code: 'IN_THE_FUTURE', detail: response })
  return out
}

// The console routes on the fragment, so an in-page link written as href="#id"
// would change the route rather than move to the field. The summary therefore
// moves focus itself and leaves the fragment alone.
function focusField(id: string): void {
  const el = document.getElementById(id)
  if (!el) return
  el.scrollIntoView({ block: 'center' })
  el.focus()
}

const ANCHOR: Record<string, string> = {
  vendorId: 'exc-vendor',
  assessmentId: 'exc-vendor',
  findingId: 'exc-finding',
  gateKey: 'exc-gate',
  title: 'exc-title',
  justification: 'exc-justification',
  compensatingControl: 'exc-control',
  ownerUserId: 'exc-owner',
  expiresOn: 'exc-expires',
  parameterCode: 'exc-parameter',
  severity: 'exc-severity',
  correctiveAction: 'exc-action',
  targetClosureOn: 'exc-target',
  vendorResponse: 'exc-response',
  vendorResponseOn: 'exc-response-on',
}

// A refusal is explained in the words of the rule that produced it. A reader
// shown "409" learns nothing about what to do next, and the one rule worth
// understanding on this screen is that the approver has to be a second person.
function DecisionRefusal({ error, onDismiss }: { error: ApiError; onDismiss: () => void }): JSX.Element {
  let heading = 'That decision was not recorded'
  let body = error.message
  let next = error.remedy

  if (error.status === 409 && error.code === 'SELF_APPROVAL') {
    heading = 'This one needs a second person'
    next = 'The same person cannot raise and approve. Ask an approver to open this one. ' + refusalReason('risk.approve')
  } else if (error.status === 409 && error.code === 'ALREADY_DECIDED') {
    heading = 'Somebody has already decided this'
    next = 'Reload the register to see who decided it and when.'
  } else if (error.status === 422) {
    heading = 'This exception cannot be approved as it stands'
    if (error.issues.length > 0) {
      body = error.issues.map((i) => i.detail).join(' ')
    }
  } else if (error.status === 403) {
    heading = 'Your roles do not include deciding'
  }

  return (
    <div className="note stop decision-note" role="alert">
      <strong>{heading}</strong>
      <p>{body}</p>
      <p className="muted">{next}</p>
      {/* The code and the reference support looks the request up by
          (release 3.1); shown here, so the feedback net adds no toast. */}
      <ErrorReferenceOf error={error} />
      <button type="button" onClick={onDismiss}>
        Dismiss
      </button>
    </div>
  )
}

// Where a pending acceptance's chain stands, as dots. Read once per pending
// row; a chain that could not be read says so rather than drawing nothing.
type ChainRead = { chain: ExceptionChain } | { failed: true }

function chainStates(chain: ExceptionChain): StageState[] {
  return chain.stages.map((st) => (st.signed ? 'done' : st.next ? 'current' : 'upcoming'))
}

// A vendor's open findings as the picker lists them. One vendor can carry
// dozens, so they are searched by title, severity or gate key rather than
// read down. The value is the finding's id, as the select's option was.
export function findingChoices(findings: Pick<Finding, 'id' | 'severity' | 'title' | 'gateKey'>[]): PickerOption[] {
  return findings.map((f) => ({ value: f.id, label: f.severity + ': ' + f.title, code: f.gateKey || undefined }))
}

export function Exceptions(): JSX.Element {
  const [me, setMe] = useState<Me | null>(null)
  const [list, setList] = useState<DDException[] | null>(null)
  // The parameters an exception can be raised under, in use ones only.
  const [params, setParams] = useState<DDParameter[]>([])
  const [vendors, setVendors] = useState<Vendor[]>([])
  const [error, setError] = useState<unknown>(null)
  const [view, setView] = useState<View>('ALL')
  const names = usePeopleNames()

  const [formOpen, setFormOpen] = useState(false)
  const formSave = useSave(formOpen, 'Raise an exception')
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT)
  const [issues, setIssues] = useState<Listed[]>([])
  const [submitError, setSubmitError] = useState<ApiError | null>(null)
  const [raised, setRaised] = useState<string | null>(null)
  const summaryRef = useRef<HTMLDivElement>(null)
  const [focusSummary, setFocusSummary] = useState(false)

  // What actually stands open against the chosen vendor. Offering the register
  // is the difference between an exception that joins back to a record
  // somebody owns and one that only reads well.
  const [openFindings, setOpenFindings] = useState<Finding[] | null>(null)

  const [openId, setOpenId] = useState<string | null>(null)
  const rowSave = useSave(openId, 'the risk acceptance')
  const [chains, setChains] = useState<Record<string, ChainRead>>({})

  const [rejectingId, setRejectingId] = useState<string | null>(null)
  const [rejectReason, setRejectReason] = useState('')
  const [rejectIssue, setRejectIssue] = useState(false)
  // The third outcome. Its own state rather than a mode on the rejection,
  // because the two say different things to the requester and a shared form
  // would need a label that says neither.
  const [remediatingId, setRemediatingId] = useState<string | null>(null)
  const [remediateNote, setRemediateNote] = useState('')
  const [remediateIssue, setRemediateIssue] = useState(false)
  const [outcome, setOutcome] = useState<{ id: string; ok?: string; error?: ApiError } | null>(null)

  const load = useCallback(() => {
    // A reader who cannot open the register (the executive) is shown the
    // exceptions waiting on them, which is what the API admits them to.
    const read = can('assessment.read')
      ? exceptionsApi.list().then((r) => r.exceptions)
      : exceptionsApi.waitingForMe().then((r) => r.exceptions.map(rowOf))
    read
      .then((rows) => {
        setList(rows)
        setError(null)
      })
      .catch((err) => {
        setList(null)
        setError(err)
      })
  }, [])

  // #/exceptions?open=<id>, which Home's regulatory clocks and the programme
  // report link to, opens that exception's drawer.
  useEffect(() => {
    const q = window.location.hash.split('?')[1] ?? ''
    const id = new URLSearchParams(q).get('open')
    if (id) setOpenId(id)
  }, [])

  useEffect(() => {
    load()
    api
      .me()
      .then(setMe)
      .catch(() => setMe(null))
    if (can('risk.accept')) {
      api
        .vendors({})
        .then((p) => setVendors(p.vendors))
        .catch(() => setVendors([]))
      exceptionsApi
        .catalogue()
        .then((c) => setParams(c.parameters.filter((x) => x.active)))
        .catch(() => setParams([]))
    }
  }, [load])

  const readChain = useCallback((id: string) => {
    api
      .exceptionChain(id)
      .then((chain) => setChains((c) => ({ ...c, [id]: { chain } })))
      .catch(() => setChains((c) => ({ ...c, [id]: { failed: true } })))
  }, [])

  // The chain for every pending row, so the register shows where each one is
  // without opening it. Settled rows are not read: their sign-off is over and
  // the row names who decided.
  useEffect(() => {
    for (const x of list ?? []) {
      if (x.status === 'PENDING' && !x.workflow) readChain(x.id)
    }
  }, [list, readChain])

  useEffect(() => {
    if (!draft.vendorId) {
      setOpenFindings(null)
      return
    }
    let live = true
    setOpenFindings(null)
    api
      .vendorFindings(draft.vendorId, { status: ['OPEN', 'IN_PROGRESS'], limit: 100 })
      .then((page) => {
        if (!live) return
        setOpenFindings(page.findings)
      })
      .catch(() => {
        if (live) {
          setOpenFindings(null)
        }
      })
    return () => {
      live = false
    }
  }, [draft.vendorId])

  const findingOptions = useMemo(() => findingChoices(openFindings ?? []), [openFindings])

  // Focus lands on the summary only after React has flushed it into the DOM.
  // Calling focus() straight after setIssues does nothing at all: the element
  // is still hidden at that point, and focus quietly falls back to the body.
  useEffect(() => {
    if (!focusSummary) return
    summaryRef.current?.focus()
    setFocusSummary(false)
  }, [focusSummary])

  const counts = useMemo(() => {
    const rows = list ?? []
    return {
      total: rows.length,
      mine: rows.filter((x) => x.workflow?.waitingForMe).length,
      pending: rows.filter(deciding).length,
      approved: rows.filter((x) => x.status === 'APPROVED').length,
      closure: rows.filter((x) => x.workflow?.closureOverdue).length,
      rejected: rows.filter((x) => x.status === 'REJECTED').length,
      lapsed: rows.filter(hasLapsed).length,
      waivers: rows.filter(isContractWaiver).length,
      waiversStanding: rows.filter(waiverStands).length,
    }
  }, [list])

  const shown = useMemo(() => {
    const rows = list ?? []
    if (view === 'ALL') return rows
    if (view === 'WAIVERS') return rows.filter(isContractWaiver)
    if (view === 'LAPSED') return rows.filter(hasLapsed)
    if (view === 'MINE') return rows.filter((x) => x.workflow?.waitingForMe)
    if (view === 'PENDING') return rows.filter(deciding)
    if (view === 'CLOSURE') return rows.filter((x) => x.workflow?.closureOverdue)
    return rows.filter((x) => x.status === view)
  }, [list, view])

  const vendorOptions = useMemo(
    () =>
      [...vendors].sort((a, b) => (a.displayName || a.legalName).localeCompare(b.displayName || b.legalName, 'en')),
    [vendors],
  )

  // An edit, and the problems that are still problems after it.
  function set<K extends keyof Draft>(key: K, value: Draft[K]) {
    const next = { ...draft, [key]: value }
    setDraft(next)
    setIssues((list) => afterEdit(list, key, checkDraft(next)))
  }

  // Choosing a finding fills the gate key from it, and the title too where
  // nothing has been typed yet. The words on the register are the words
  // somebody looks for a year later, and retyping them is how one record stops
  // matching the other.
  function chooseFinding(id: string) {
    const found = (openFindings ?? []).find((f) => f.id === id)
    // The finding's severity and risk type fill the severity and the
    // parameter where nothing is chosen yet: one taxonomy, so the finding
    // already says which parameter it falls under.
    const byType = found?.riskType ? params.find((p) => p.riskTypes.includes(found.riskType ?? '')) : undefined
    setDraft((d) => ({
      ...d,
      findingId: id,
      gateKey: id === '' ? d.gateKey : (found?.gateKey ?? ''),
      title: d.title.trim() === '' && found ? found.title : d.title,
      severity: d.severity || (found && SEVERITIES.some((x) => x.code === found.severity) ? found.severity : ''),
      parameterCode: d.parameterCode || (byType?.code ?? ''),
    }))
  }

  function issueFor(field: string): Issue | undefined {
    return issues.find((i) => i.field === field)
  }

  // A date's rule is said when the person leaves it; the other fields wait
  // for Save, as they did.
  function leaveDate(field: string) {
    const found = dateIssues(draft).filter((i) => i.field === field)
    setIssues((cur) => [...cur.filter((i) => i.field !== field), ...found])
  }

  function submit(e: FormEvent) {
    e.preventDefault()
    setRaised(null)
    setSubmitError(null)
    const found = checkDraft(draft)
    setIssues(found)
    if (found.length > 0) {
      // Focus moves to the summary only after a failed submit, the one moment
      // a reader has asked for the verdict. It is never moved while somebody
      // is still typing.
      setFocusSummary(true)
      return
    }
    formSave.send(exceptionsApi.create(exceptionBody(draft)), {
      done: (reply) => {
        setDraft(EMPTY_DRAFT)
        setIssues([])
        setFormOpen(false)
        // The API's own sentence where it sent one: it names the expiry it
        // recorded and what approving this will do to the finding, neither of
        // which this form decided.
        setRaised(reply.note || 'Raised. Nothing is accepted until the authority for its severity approves it.')
        load()
      },
      failed: (err) => {
        const ae = err instanceof ApiError ? err : new ApiError(0, 'UNKNOWN', String(err))
        if (ae.status === 422 && ae.issues.length > 0) {
          setIssues(fromServer(ae.issues))
          setFocusSummary(true)
        } else {
          setSubmitError(ae)
        }
      },
    })
  }


  // Approving is not here any more. It became the act of signing a stage of
  // section 14's chain when that landed, and it lives in ExceptionChainPanel;
  // what is left are the two refusals, which are the same call with a different
  // word and must keep sharing the maker-checker rule.
  // One of the open record's two decisions, from its reason box.
  function decide(kind: 'reject' | 'remediate') {
    if (!open) return
    const x = open
    const plan = decideAcceptance(kind, x.id, kind === 'reject' ? rejectReason : remediateNote)
    if (kind === 'reject') setRejectIssue(plan.missing)
    else setRemediateIssue(plan.missing)
    if (plan.missing) return
    setOutcome(null)
    rowSave.send(plan.work, {
      done: (reply) => {
        setOutcome({
          id: x.id,
          // The server's own sentence, which names what the decision did to the
          // finding this exception carries. It sends one only where there is a
          // finding, so the fallback claims nothing about the register: it says
          // only what this screen knows without being told.
          ok:
            reply.note ||
            (kind === 'reject'
              ? 'Rejected. The gap stands.'
              : 'Changes requested. The gap stands and the work asked for is in the note.'),
        })
        setRejectingId(null)
        setRejectReason('')
        setRemediatingId(null)
        setRemediateNote('')
        load()
      },
      failed: (err) => setOutcome({ id: x.id, error: err instanceof ApiError ? err : new ApiError(0, 'UNKNOWN', String(err)) }),
    })
  }


  function closeDrawer() {
    // A stage signed inside the drawer moves the chain without granting the
    // acceptance, so the row's dots are read again on the way out.
    const was = list?.find((x) => x.id === openId)
    if (openId && was?.status === 'PENDING' && !was.workflow) readChain(openId)
    setOpenId(null)
    setRejectingId(null)
    setRemediatingId(null)
  }

  const canDecide = can('risk.approve')
  // Somebody who may read the register. The executive may not, and is shown
  // only the exceptions waiting on them, with no filters to choose between.
  const reader = can('assessment.read')
  const chosenParam = params.find((p) => p.code === draft.parameterCode)
  const canRaise = can('risk.accept')
  const open = openId ? (list ?? []).find((x) => x.id === openId) ?? null : null
  // The open record's own decisions, which its dialog draws in the footer. A
  // record on a workflow is decided stage by stage in the panel inside it.
  const decidable = open !== null && open.status === 'PENDING' && !open.workflow
  const rejecting = open !== null && rejectingId === open.id
  const remediating = open !== null && remediatingId === open.id

  const tiles: { key: View; n: number; label: string; hot?: boolean }[] = [
    { key: 'ALL', n: counts.total, label: 'On the register' },
    { key: 'MINE', n: counts.mine, label: 'Waiting for you', hot: counts.mine > 0 },
    { key: 'PENDING', n: counts.pending, label: 'Pending or in review' },
    { key: 'APPROVED', n: counts.approved, label: 'Approved' },
    { key: 'CLOSURE', n: counts.closure, label: 'Closure overdue', hot: counts.closure > 0 },
    { key: 'LAPSED', n: counts.lapsed, label: 'Expired', hot: counts.lapsed > 0 },
    { key: 'REJECTED', n: counts.rejected, label: 'Rejected' },
    // Every contract waiver, the standing ones counted in the label: a waiver
    // stands in for a contract nobody has filed, so it stays in sight.
    {
      key: 'WAIVERS',
      n: counts.waivers,
      label: counts.waiversStanding > 0 ? 'Contract waivers, ' + counts.waiversStanding + ' standing' : 'Contract waivers',
      hot: counts.waiversStanding > 0,
    },
  ]

  const columns: Column<DDException>[] = [
    {
      key: 'risk',
      header: 'Risk',
      sortValue: (x) => x.title,
      render: (x) => (
        <>
          <button type="button" className="prose-rowlink prose-clamp" onClick={() => setOpenId(x.id)}>
            {x.title}
          </button>
          <span className="prose-sub">
            {x.vendorName || 'Vendor'}
            {x.gateKey ? ', ' : ''}
            {x.gateKey && <span className="prose-mono">{x.gateKey}</span>}
          </span>
          {/* What the record is. An exception about one gap needs no label;
              a risk acceptance names the rating it accepts, and a contract
              waiver says it is one, so either can be found on sight. */}
          {isVendorWide(x) && (
            <span className="prose-sub" data-testid="exception-kind">
              <Status tone={isContractWaiver(x) ? 'warn' : 'special'}>{kindLabel(x)}</Status>
              {isRiskAcceptance(x) && ' ' + ratingPhrase(x.acceptsBand)}
            </span>
          )}
          {x.workflow && (
            <span className="prose-sub">
              <Status tone={severityTone(x.workflow.severity)}>{x.workflow.severityName}</Status>{' '}
              {x.workflow.parameterName}
            </span>
          )}
        </>
      ),
    },
    {
      key: 'signoff',
      header: 'Stage',
      render: (x) => {
        if (x.workflow) {
          const wf = x.workflow
          const at = wf.track.find((t) => t.state === 'current' || t.state === 'stopped')
          return (
            <>
              <MiniTrack states={trackStates(wf)} label={wf.statusName + (at ? ', at ' + at.label : '')} />
              <span className="prose-sub">{at ? at.label : wf.statusName}</span>
            </>
          )
        }
        if (x.status !== 'PENDING') {
          return x.decidedAt ? (
            <>
              <span>Decided</span>
              <span className="prose-sub prose-mono">{shortDate(x.decidedAt)}</span>
            </>
          ) : (
            <span className="prose-sub">Not recorded</span>
          )
        }
        const read = chains[x.id]
        if (!read) return <span className="prose-sub">Reading</span>
        if ('failed' in read) return <Status tone="neutral">Not read</Status>
        const { chain } = read
        if (chain.noRow) return <Status tone="warn">No chain</Status>
        const signed = chain.stages.filter((st) => st.signed).length
        return (
          <>
            <MiniTrack
              states={chainStates(chain)}
              label={signed + ' of ' + chain.stages.length + ' signatures'}
            />
            <span className="prose-sub">
              {signed} of {chain.stages.length} signed
            </span>
          </>
        )
      },
    },
    {
      key: 'who',
      header: 'Reviews and decides',
      render: (x) => {
        const wf = x.workflow
        if (!wf) {
          // An exception raised before the workflow: its approval chain.
          return x.decidedBy ? (
            <span className="dd-who">
              <span>
                Decided by <b>{personLabel(names, x.decidedBy)}</b>
              </span>
            </span>
          ) : (
            <span className="prose-sub">Approval chain</span>
          )
        }
        return (
          <span className="dd-who">
            <span>
              Reviews: <b>{wf.reviewerName || 'not routed'}</b>
            </span>
            <span>
              Decides: <b>{wf.decision ? personaNames(wf.decision.deciders) : '-'}</b>
            </span>
            {wf.waitingForMe && <Status tone="brand">Waiting for you</Status>}
          </span>
        )
      },
    },
    {
      key: 'closure',
      header: 'Closure due',
      sortValue: (x) => x.workflow?.targetClosureOn ?? '',
      render: (x) => {
        const wf = x.workflow
        if (!wf?.targetClosureOn) return <span className="prose-sub">-</span>
        return (
          <>
            <span className="prose-mono prose-nowrap">{shortDate(wf.targetClosureOn)}</span>
            {wf.closureOverdue && (
              <span className="prose-sub">
                <Status tone="crit">Overdue</Status>
              </span>
            )}
          </>
        )
      },
    },
    {
      key: 'expiry',
      header: 'Expiry',
      sortValue: (x) => x.expiresOn,
      render: (x) => {
        const e = expiryOf(x)
        return (
          <>
            <span className="prose-mono prose-nowrap">{shortDate(x.expiresOn)}</span>
            {e && (
              <span className="prose-sub">
                <Status tone={e.tone}>{e.text}</Status>
              </span>
            )}
          </>
        )
      },
    },
    {
      key: 'status',
      header: 'Status',
      sortValue: (x) => statusOf(x).text,
      render: (x) => {
        const s = statusOf(x)
        return <Status tone={s.tone}>{s.text}</Status>
      },
    },
  ]

  const openStatus = open ? statusOf(open) : null
  const openExpiry = open ? expiryOf(open) : null
  const mine = open != null && me != null && me.subject === open.requestedBy

  return (
    <div className="prose-page">
      <AssessTabs at="exceptions" />
      <div className="prose-head">
        <div className="prose-head-main">
          {/* No subtitle: the pending and lapsed counts are the tiles below,
              and the Lapsed tile is how a reader reaches the lapsed ones. */}
          <h1>Risk acceptances</h1>
        </div>
        {can('assessment.read') && <div className="prose-actions">
          {/* Open to everyone, as the inline form was: a reader without the
              capability can see what raising asks for, and the submit inside
              is what is refused. */}
          <button
            type="button"
            className="primary"
            onClick={() => {
              setSubmitError(null)
              setFormOpen(true)
            }}
          >
            Raise an exception
          </button>
          {!canRaise && <Toggletip label="Why you cannot raise one">{tip(refusalReason('risk.accept'))}</Toggletip>}
        </div>}
      </div>

      {error != null && <ErrorBox error={error} what="The exception register" />}

      {raised && (
        <p className="prose-line" role="status">
          <Status tone="ok">Raised</Status>
          {raised.includes('for review') ? 'Sent for review' : 'Waiting for its clarification'}
          <Toggletip label="What was recorded">{tip(raised)}</Toggletip>
        </p>
      )}

      {list && reader && (
        <div className="prose-tiles" role="group" aria-label="Filter the register">
          {tiles.map((t) => (
            <button
              key={t.key}
              type="button"
              className={'prose-tile' + (t.hot ? ' prose-hot' : '')}
              aria-pressed={view === t.key}
              aria-label={t.label + ': ' + t.n}
              onClick={() => setView(t.key)}
            >
              <span className="prose-tile-n">{t.n}</span>
              <span className="prose-tile-l">{t.label}</span>
            </button>
          ))}
        </div>
      )}

      {outcome && outcome.ok && (openId !== outcome.id || open === null) && (
        <p className="prose-line" role="status">
          <Status tone="ok">Recorded</Status>
          Decision saved
          <Toggletip label="What the decision did">{tip(outcome.ok)}</Toggletip>
        </p>
      )}

      <section className="panel panel-flush" aria-labelledby="register-heading">
        <div className="prose-panelhead">
          <h2 id="register-heading">Register</h2>
        </div>

        {!list && error == null && <Loading what="the exception register" />}

        {list && (
          <DataTable
            caption="Exceptions, risk acceptances and contract waivers, soonest expiry first"
            columns={columns}
            rows={shown}
            rowKey={(x) => x.id}
            empty={{
              title: !reader
                ? 'Nothing is waiting for your decision.'
                : counts.total === 0
                  ? 'No exception has been raised.'
                  : 'No exception matches this filter.',
              detail: !reader
                ? 'An exception waiting for you appears here and in the programme report.'
                : counts.total === 0
                  ? 'Raise one here when a gate fires, not in mail.'
                  : 'Choose On the register to see them all.',
            }}
            footer={<ListCount shown={shown.length} total={counts.total} noun="acceptances" />}
          />
        )}
      </section>

      <Dialog
        shape="drawer"
        kind="record"
        open={open !== null}
        onClose={closeDrawer}
        title={open?.title ?? 'Risk acceptance'}
        {...acceptanceFooter(
          { id: open?.id ?? '', rejecting, remediating, rejectIssue, remediateIssue, busy: rowSave.busy },
          { reject: () => setRejectingId(null), remediate: () => setRemediatingId(null) },
        )}
        more={
          open && decidable && !rejecting && !remediating ? (
            <>
              <button
                type="button"
                disabled={!canDecide}
                title={canDecide ? undefined : refusalReason('risk.approve')}
                onClick={() => {
                  setRejectingId(null)
                  setRemediatingId(open.id)
                  setRemediateNote('')
                  setRemediateIssue(false)
                }}
              >
                Send back for remediation
              </button>
              <button
                type="button"
                className="danger"
                disabled={!canDecide}
                title={canDecide ? undefined : refusalReason('risk.approve')}
                onClick={() => {
                  setRemediatingId(null)
                  setRejectingId(open.id)
                  setRejectReason('')
                  setRejectIssue(false)
                }}
              >
                Reject
              </button>
              {mine && (
                <Toggletip label="Why you cannot decide this one">
                  You raised this one, so you may not decide it. Every stage of it is for somebody else.
                </Toggletip>
              )}
              {!canDecide && <Toggletip label="Why you cannot decide">{tip(refusalReason('risk.approve'))}</Toggletip>}
            </>
          ) : undefined
        }
      >
        {open && (
          <div className="prose-drawer-stack">
            <div className="prose-chips">
              {isVendorWide(open) && <Status tone={isContractWaiver(open) ? 'warn' : 'special'}>{kindLabel(open)}</Status>}
              {openStatus && <Status tone={openStatus.tone}>{openStatus.text}</Status>}
              {openExpiry && <Status tone={openExpiry.tone}>{openExpiry.text}</Status>}
            </div>

            <dl className="prose-kv">
              <dt>Vendor</dt>
              <dd>
                <a href={'#/vendor/' + encodeURIComponent(open.vendorId)}>{open.vendorName || 'Open the vendor'}</a>
              </dd>
              {/* A risk acceptance and a contract waiver are about the vendor
                  as a whole, so they name no gate and no finding: what they
                  accept or stand in for is said instead, and the chip above
                  says which kind it is. */}
              {isVendorWide(open) ? (
                <>
                  {isRiskAcceptance(open) && (
                    <>
                      <dt>Accepts</dt>
                      <dd>
                        {ratingWords(open.acceptsBand) === 'Not yet rated'
                          ? 'The rating of a vendor not yet rated, treated as Critical'
                          : 'The ' + ratingWords(open.acceptsBand) + ' rating'}
                      </dd>
                    </>
                  )}
                  {isContractWaiver(open) && (
                    <>
                      <dt>Stands in for</dt>
                      <dd>The contract, until {shortDate(open.expiresOn)} or until the contract is filed</dd>
                    </>
                  )}
                </>
              ) : (
                <>
                  <dt>Gate</dt>
                  <dd className="prose-mono">{open.gateKey || 'Not linked to a gate'}</dd>
                  <dt>Finding</dt>
                  <dd>{open.findingId ? <a href="#/findings">A finding on the register</a> : 'Against no recorded finding'}</dd>
                </>
              )}
              {open.ownerUserId && (
                <>
                  <dt>Owner</dt>
                  <dd>{personLabel(names, open.ownerUserId)}</dd>
                </>
              )}
              <dt>Raised by</dt>
              <dd className="prose-mono">{open.requestedBy}</dd>
              {open.decidedBy && (
                <>
                  <dt>Decided by</dt>
                  <dd>
                    <span className="prose-mono">{open.decidedBy}</span>
                    {open.decidedAt ? ', ' + shortDate(open.decidedAt) : ''}
                  </dd>
                </>
              )}
              <dt>Expires</dt>
              <dd className="prose-mono">{shortDate(open.expiresOn)}</dd>
            </dl>

            {hasLapsed(open) && (
              <div className="note stop">
                <strong>{open.status === 'EXPIRED' ? 'The acceptance has ended' : 'Expired and still approved'}</strong>
                <p style={{ margin: 0 }}>
                  It ran out on {shortDate(open.expiresOn)}.{' '}
                  {open.status === 'EXPIRED'
                    ? 'The finding it was holding is open again and back with its owner. Raise a replacement with a real expiry, or close the finding.'
                    : open.findingId
                      ? 'It still reads as approved: the sweep that ends an acceptance and hands its finding back runs hourly and has not reached this one.'
                      : 'Nothing will end it, because it names no finding to hand back. Either the gap was closed and this record was never closed with it, or it is being run with nobody accountable.'}
                </p>
              </div>
            )}

            {open.justification && (
              <div>
                <h3>{isVendorWide(open) ? 'Reason' : 'Justification'}</h3>
                <p className="prose-quote">{open.justification}</p>
              </div>
            )}
            {open.compensatingControl && (
              <div>
                <h3>Compensating control</h3>
                <p className="prose-quote">{open.compensatingControl}</p>
              </div>
            )}

            {open.workflow && (
              <ExceptionWorkflowPanel
                key={open.id}
                exceptionId={open.id}
                save={rowSave.of('workflow')}
                onChanged={(note) => {
                  setOutcome({ id: open.id, ok: note })
                  load()
                }}
              />
            )}

            {open.status === 'PENDING' && !open.workflow && (
              <div className="prose-drawer-chain">
                <h3>Sign-off</h3>
                <ExceptionChainPanel
                  key={open.id}
                  exceptionId={open.id}
                  mine={mine}
                  save={rowSave.of('sign')}
                  onGranted={(note) => {
                    setOutcome({ id: open.id, ok: note })
                    load()
                  }}
                />
              </div>
            )}

            {remediatingId === open.id && (
              <form
                className="reject-form"
                id={'remediate-' + open.id}
                onSubmit={(e) => {
                  e.preventDefault()
                  decide('remediate')
                }}
              >
                <label htmlFor={'remediate-note-' + open.id}>What has to be done first</label>
                <div className="reject-row">
                  <input
                    id={'remediate-note-' + open.id}
                    type="text"
                    autoFocus
                    value={remediateNote}
                    aria-invalid={remediateIssue ? true : undefined}
                    aria-describedby={remediateIssue ? 'remediate-issue-' + open.id : undefined}
                    onChange={(e) => {
                      setRemediateNote(e.target.value)
                      if (e.target.value.trim()) setRemediateIssue(false)
                    }}
                  />
                </div>
                {remediateIssue && (
                  <p className="field-issue" id={'remediate-issue-' + open.id}>
                    Name the work. That is the point of sending it back rather than rejecting it.
                  </p>
                )}
              </form>
            )}

            {rejectingId === open.id && (
              <form
                className="reject-form"
                id={'reject-' + open.id}
                onSubmit={(e) => {
                  e.preventDefault()
                  decide('reject')
                }}
              >
                <label htmlFor={'reason-' + open.id}>Reason for rejection</label>
                <div className="reject-row">
                  <input
                    id={'reason-' + open.id}
                    type="text"
                    autoFocus
                    value={rejectReason}
                    aria-invalid={rejectIssue ? true : undefined}
                    aria-describedby={rejectIssue ? 'reason-issue-' + open.id : undefined}
                    onChange={(e) => {
                      setRejectReason(e.target.value)
                      if (e.target.value.trim()) setRejectIssue(false)
                    }}
                  />
                </div>
                {rejectIssue && (
                  <p className="field-issue" id={'reason-issue-' + open.id}>
                    A reason is required before an exception can be rejected.
                  </p>
                )}
              </form>
            )}

            {outcome?.id === open.id && outcome.ok && (
              <p className="ok-note" role="status">
                {outcome.ok}
              </p>
            )}
            {outcome?.id === open.id && outcome.error && (
              <DecisionRefusal error={outcome.error} onDismiss={() => setOutcome(null)} />
            )}
          </div>
        )}
      </Dialog>

      <Dialog
        shape="drawer"
        kind="form"
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title="Raise an exception"
        busy={formSave.busy}
        problems={issues.length}
        primary={{
          label: formSave.busy ? 'Raising' : 'Raise the exception',
          form: 'raise-form',
          off: !canRaise && refusalReason('risk.accept'),
          actionId: 'tprm.exceptions.a07',
        }}
        more={
          <>
            <button
              type="button"
              onClick={() => {
                setDraft(EMPTY_DRAFT)
                setIssues([])
                setSubmitError(null)
              }}
            >
              Clear
            </button>
            <Toggletip label="What raising does">
              Raising records it. The parameter's function reviews it and the authority for its severity decides.
            </Toggletip>
          </>
        }
      >
        <form id="raise-form" onSubmit={submit} noValidate className="prose-drawer-stack">
          <div
            className="form-errors"
            ref={summaryRef}
            tabIndex={-1}
            role={issues.length > 0 ? 'alert' : undefined}
            hidden={issues.length === 0}
          >
            <h3>
              {issues.length} thing{issues.length === 1 ? '' : 's'} to fix before this can be raised
            </h3>
            <ol>
              {issues.map((i) => (
                <li key={i.field + i.code}>
                  <button type="button" className="linkish" onClick={() => focusField(ANCHOR[i.field] ?? 'exc-vendor')}>
                    {i.detail}
                  </button>
                </li>
              ))}
            </ol>
          </div>

          {/* Not ErrorBox: this was an action that was attempted and refused,
              not a panel that failed to load. */}
          {submitError && (
            <div className="error-box" role="alert">
              <h3>The exception was not raised ({submitError.code})</h3>
              <p>{submitError.message}</p>
              <p className="muted">{submitError.remedy}</p>
            </div>
          )}

          <div className="field">
            <label htmlFor="exc-vendor">Vendor</label>
            <VendorChoice
              bare
              browse
              id="exc-vendor"
              label="Vendor"
              value={draft.vendorId}
              options={vendorOptions}
              invalid={Boolean(issueFor('vendorId'))}
              describedBy={issueFor('vendorId') ? 'exc-vendor-issue' : undefined}
              onChange={(id) => set('vendorId', id)}
            />
            {issueFor('vendorId') && (
              <p className="field-issue" id="exc-vendor-issue">
                {issueFor('vendorId')?.detail}
              </p>
            )}
          </div>

          <div className="field">
            <label htmlFor="exc-finding">The finding this accepts</label>
            <Picker
              bare
              id="exc-finding"
              label="The finding this accepts"
              value={draft.findingId}
              options={findingOptions}
              none="Not against a recorded finding"
              placeholder="Search this vendor's open findings"
              noun="finding"
              missing="A finding not in this list"
              onChange={chooseFinding}
            />
          </div>

          <div className="dd-form-row">
            <div className="field">
              <label htmlFor="exc-parameter">Due diligence parameter</label>
              <select
                id="exc-parameter"
                value={draft.parameterCode}
                aria-invalid={issueFor('parameterCode') ? true : undefined}
                aria-describedby={issueFor('parameterCode') ? 'exc-parameter-issue' : undefined}
                onChange={(e) => set('parameterCode', e.target.value)}
              >
                <option value="">Choose a parameter</option>
                {params.map((p) => (
                  <option key={p.code} value={p.code}>
                    {p.name}
                  </option>
                ))}
              </select>
              {issueFor('parameterCode') && (
                <p className="field-issue" id="exc-parameter-issue">
                  {issueFor('parameterCode')?.detail}
                </p>
              )}
              {chosenParam && <span className="field-note">Reviewed by the {chosenParam.reviewerName ?? chosenParam.reviewerPersona}</span>}
            </div>
            <div className="field">
              <label htmlFor="exc-severity">Severity</label>
              <select
                id="exc-severity"
                value={draft.severity}
                aria-invalid={issueFor('severity') ? true : undefined}
                aria-describedby={issueFor('severity') ? 'exc-severity-issue' : undefined}
                onChange={(e) => set('severity', e.target.value)}
              >
                <option value="">Choose a severity</option>
                {SEVERITIES.map((x) => (
                  <option key={x.code} value={x.code}>
                    {x.name}
                  </option>
                ))}
              </select>
              {issueFor('severity') && (
                <p className="field-issue" id="exc-severity-issue">
                  {issueFor('severity')?.detail}
                </p>
              )}
            </div>
          </div>

          <div className="field">
            <label htmlFor="exc-gate">Gate key</label>
            <input
              id="exc-gate"
              type="text"
              list="exc-gate-list"
              value={draft.gateKey}
              placeholder="DPDPA-PROC-01"
              onChange={(e) => set('gateKey', e.target.value.toUpperCase())}
            />
            <datalist id="exc-gate-list">
              {CATALOGUE_GATES.map((g) => (
                <option key={g.key} value={g.key}>
                  {g.what}
                </option>
              ))}
            </datalist>
          </div>

          <div className="field">
            <label htmlFor="exc-title">Title</label>
            <input
              id="exc-title"
              type="text"
              value={draft.title}
              placeholder="No written processing agreement for the payroll feed"
              aria-invalid={issueFor('title') ? true : undefined}
              aria-describedby={issueFor('title') ? 'exc-title-issue' : undefined}
              onChange={(e) => set('title', e.target.value)}
            />
            {issueFor('title') && (
              <p className="field-issue" id="exc-title-issue">
                {issueFor('title')?.detail}
              </p>
            )}
          </div>

          <div className="field">
            <label htmlFor="exc-justification">Justification</label>
            <textarea
              id="exc-justification"
              rows={3}
              value={draft.justification}
              aria-invalid={issueFor('justification') ? true : undefined}
              aria-describedby={issueFor('justification') ? 'exc-justification-issue' : undefined}
              onChange={(e) => set('justification', e.target.value)}
            />
            {issueFor('justification') && (
              <p className="field-issue" id="exc-justification-issue">
                {issueFor('justification')?.detail}
              </p>
            )}
          </div>

          <div className="field">
            <label htmlFor="exc-control">Compensating control</label>
            <textarea
              id="exc-control"
              rows={3}
              value={draft.compensatingControl}
              aria-invalid={issueFor('compensatingControl') ? true : undefined}
              aria-describedby={issueFor('compensatingControl') ? 'exc-control-issue' : undefined}
              onChange={(e) => set('compensatingControl', e.target.value)}
            />
            {issueFor('compensatingControl') && (
              <p className="field-issue" id="exc-control-issue">
                {issueFor('compensatingControl')?.detail}
              </p>
            )}
          </div>

          <div className="field">
            <label htmlFor="exc-owner">Owner</label>
            <input
              id="exc-owner"
              type="text"
              value={draft.ownerUserId}
              placeholder="The person who owns this until it expires"
              aria-invalid={issueFor('ownerUserId') ? true : undefined}
              aria-describedby={issueFor('ownerUserId') ? 'exc-owner-issue' : undefined}
              onChange={(e) => set('ownerUserId', e.target.value)}
            />
            {issueFor('ownerUserId') && (
              <p className="field-issue" id="exc-owner-issue">
                {issueFor('ownerUserId')?.detail}
              </p>
            )}
          </div>

          <div className="field">
            <label htmlFor="exc-expires">Expires on</label>
            <input
              id="exc-expires"
              type="date"
              value={draft.expiresOn}
              min={isoDay(1)}
              aria-invalid={issueFor('expiresOn') ? true : undefined}
              aria-describedby={issueFor('expiresOn') ? 'exc-expires-issue' : undefined}
              onChange={(e) => set('expiresOn', e.target.value)}
              onBlur={() => leaveDate('expiresOn')}
            />
            {issueFor('expiresOn') && (
              <p className="field-issue" id="exc-expires-issue" role="alert">
                {issueFor('expiresOn')?.detail}
              </p>
            )}
          </div>

          <div className="dd-section dd-form" role="group" aria-labelledby="exc-clarify-h">
            <h3 id="exc-clarify-h" className="sw-row">
              The vendor's clarification
              <Toggletip label="When it goes for review">
                With the corrective action and the target closure date it goes straight to its reviewer. Without them it
                waits here until they are recorded.
              </Toggletip>
            </h3>
            <div className="field">
              <label htmlFor="exc-action">Corrective action</label>
              <textarea id="exc-action" rows={2} value={draft.correctiveAction} onChange={(e) => set('correctiveAction', e.target.value)} />
            </div>
            <div className="dd-form-row">
              <div className="field">
                <label htmlFor="exc-target">Target closure date</label>
                <input
                  id="exc-target"
                  type="date"
                  value={draft.targetClosureOn}
                  min={isoDay(0)}
                  max={draft.expiresOn || undefined}
                  aria-invalid={issueFor('targetClosureOn') ? true : undefined}
                  aria-describedby={issueFor('targetClosureOn') ? 'exc-target-issue' : undefined}
                  onChange={(e) => set('targetClosureOn', e.target.value)}
                  onBlur={() => leaveDate('targetClosureOn')}
                />
                {issueFor('targetClosureOn') && (
                  <p className="field-issue" id="exc-target-issue" role="alert">
                    {issueFor('targetClosureOn')?.detail}
                  </p>
                )}
              </div>
              <div className="field">
                <label htmlFor="exc-response-on">Response received on</label>
                <input
                  id="exc-response-on"
                  type="date"
                  value={draft.vendorResponseOn}
                  max={isoDay(0)}
                  aria-invalid={issueFor('vendorResponseOn') ? true : undefined}
                  aria-describedby={issueFor('vendorResponseOn') ? 'exc-response-on-issue' : undefined}
                  onChange={(e) => set('vendorResponseOn', e.target.value)}
                  onBlur={() => leaveDate('vendorResponseOn')}
                />
                {issueFor('vendorResponseOn') && (
                  <p className="field-issue" id="exc-response-on-issue" role="alert">
                    {issueFor('vendorResponseOn')?.detail}
                  </p>
                )}
              </div>
            </div>
            <div className="field">
              <label htmlFor="exc-response">Vendor's response</label>
              <textarea id="exc-response" rows={2} value={draft.vendorResponse} onChange={(e) => set('vendorResponse', e.target.value)} />
            </div>
          </div>

        </form>
      </Dialog>
    </div>
  )
}
